import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AuthState, UserProfile, CompanyAccess, AuthError } from '@/types/auth'

interface AuthActions {
  setAuthenticated: (user: UserProfile, companies?: CompanyAccess[]) => void
  setRequires2FA: (token: string) => void
  complete2FA: (user: UserProfile) => void
  setCurrentCompany: (companyId: string) => void
  setDepartment: (departmentId: string | null, departmentName: string | null) => void
  logout: () => void
  setError: (error: AuthError | null) => void
  clearError: () => void
  setLoading: (loading: boolean) => void
}

type AuthStore = AuthState & AuthActions

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  user: null,
  companies: [],
  currentCompanyId: null,
  requires2FA: false,
  twoFactorToken: null,
  error: null,
  departmentId: null,
  departmentName: null,
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      setAuthenticated: (user, companies = []) =>
        set({
          isAuthenticated: true,
          isLoading: false,
          user,
          companies,
          currentCompanyId: user.company_id,
          requires2FA: false,
          twoFactorToken: null,
          error: null,
        }),

      setRequires2FA: (token) =>
        set({
          requires2FA: true,
          twoFactorToken: token,
          isLoading: false,
        }),

      complete2FA: (user) =>
        set({
          isAuthenticated: true,
          requires2FA: false,
          twoFactorToken: null,
          user,
          currentCompanyId: user.company_id,
        }),

      setCurrentCompany: (companyId) =>
        set({ currentCompanyId: companyId }),

      setDepartment: (departmentId, departmentName) =>
        set({ departmentId, departmentName }),

      logout: () =>
        set({ ...initialState }),

      setError: (error) =>
        set({ error, isLoading: false }),

      clearError: () =>
        set({ error: null }),

      setLoading: (loading) =>
        set({ isLoading: loading }),
    }),
    {
      name: 'bheem-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        companies: state.companies,
        currentCompanyId: state.currentCompanyId,
        departmentId: state.departmentId,
        departmentName: state.departmentName,
      }),
    }
  )
)

// Selectors
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated
export const selectUser = (state: AuthStore) => state.user
export const selectCurrentCompany = (state: AuthStore) =>
  state.companies.find((c) => c.company_id === state.currentCompanyId)
export const selectRequires2FA = (state: AuthStore) => state.requires2FA
export const selectAccessibleModules = (state: AuthStore) =>
  state.user?.accessible_modules || []
export const selectDepartment = (state: AuthStore) => ({
  departmentId: state.departmentId,
  departmentName: state.departmentName,
})
